import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { testingService, nexiGlobalTemplateService } from '../../services/api';
import ApiTestPlanPanel from './ApiTestPlanPanel';
import ApiTestCasePickerModal from './ApiTestCasePickerModal';
import ApiTestRunReportModal from './ApiTestRunReportModal';
import RunReportComparePanel from './RunReportComparePanel';
import { buildReportDrivenPlanCase } from './buildReportCaseUserMessages';
import { RERUN_MESSAGE_SOURCE } from './rerunMessageSource';
import { setReportCaseReviewed } from './reportCaseReview';
import { resolvePlanCaseIndex } from './resolveReportPlanCase';
import { consumePendingTestPlanImport } from './pendingTestPlanImport';
import { ReactComponent as PlayIcon } from '../../assets/play.svg';

// Roll up per-case statuses into the summary counts shown in the report header.
// Shared by the persisted-payload builder and the single-case re-run flow.
const summarizeRunReportCases = (cases) => {
  let completed = 0;
  let failed = 0;
  let skipped = 0;
  (cases || []).forEach((c) => {
    if (c?.status === 'completed') completed += 1;
    else if (c?.status === 'failed') failed += 1;
    else if (c?.status === 'skipped') skipped += 1;
  });
  return { total: (cases || []).length, completed, failed, skipped };
};

const matchReportCase = (reportCase, targetCase) => {
  const sameByIndex =
    Number.isInteger(targetCase?.case_index) && reportCase?.case_index === targetCase.case_index;
  const sameByName = targetCase?.name && reportCase?.name === targetCase.name;
  return sameByIndex || sameByName;
};

const mutateReportExchanges = (report, caseItem, exchangeIndex, mutation) => {
  if (!report) return report;
  const updatedCases = (report.cases || []).map((c) => {
    if (!matchReportCase(c, caseItem)) return c;
    const exchanges = [...(Array.isArray(c.exchanges) ? c.exchanges : [])];
    if (mutation.action === 'delete') {
      if (exchangeIndex < 0 || exchangeIndex >= exchanges.length) return c;
      exchanges.splice(exchangeIndex, 1);
    } else if (mutation.action === 'update') {
      if (exchangeIndex < 0 || exchangeIndex >= exchanges.length) return c;
      exchanges[exchangeIndex] = {
        ...exchanges[exchangeIndex],
        message: mutation.message ?? exchanges[exchangeIndex].message,
        answer: mutation.answer ?? exchanges[exchangeIndex].answer,
      };
    }
    return { ...c, exchanges };
  });
  return { ...report, cases: updatedCases };
};

const removeReportCase = (report, caseItem) => {
  if (!report) return report;
  const cases = Array.isArray(report.cases) ? report.cases : [];
  const updatedCases = cases.filter((c) => !matchReportCase(c, caseItem));
  const counts = summarizeRunReportCases(updatedCases);
  const nextStatus = updatedCases.length === 0
    ? 'completed'
    : counts.failed > 0
      ? 'completed_with_failures'
      : 'completed';
  return {
    ...report,
    cases: updatedCases,
    status: nextStatus,
    finished_at: report?.finished_at || new Date().toISOString(),
    case_total: counts.total,
    case_completed: counts.completed,
    case_failed: counts.failed,
    case_skipped: counts.skipped,
  };
};

// Build the persisted-run-report payload from any in-memory run report object.
// Defined at module scope so handleRunPlan can auto-save using the just-finished
// `nextReport` directly (bypassing the React state update timing).
const computeRunReportPayload = (report, { clinicId, planId, notesOverride, nameOverride } = {}) => {
  if (!report) return null;
  const { total, completed, failed, skipped } = summarizeRunReportCases(report.cases);
  const planName =
    typeof nameOverride === 'string' && nameOverride.trim()
      ? nameOverride.trim()
      : report.plan_name || 'Plan';
  const notes =
    typeof notesOverride === 'string' ? notesOverride : report.notes || '';
  const reportSnapshot = { ...report, plan_name: planName, notes };
  return {
    clid: report.clid || clinicId || '',
    plan_id: report.plan_id || planId || null,
    plan_name: planName,
    status: report.status || 'completed',
    started_at: report.started_at,
    finished_at: report.finished_at,
    case_total: total,
    case_completed: completed,
    case_failed: failed,
    case_skipped: skipped,
    selected_case_indices: Array.isArray(report.selected_case_indices)
      ? report.selected_case_indices
      : [],
    report: reportSnapshot,
    notes,
  };
};

// Resolve and display template reply (content + metadata). Defined at module level so it
// is not recreated on every parent render; otherwise React unmounts/remounts and state resets.
const TemplatePreview = ({ templateCall, clid, onQuickReplyClick, isSubmitting: submitting }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const templateKey = templateCall?.template_name
    ? `${templateCall.template_name}|${templateCall.language_preference ?? 'zh_HK'}|${JSON.stringify(templateCall.content_variables || {})}`
    : '';
  useEffect(() => {
    if (!templateKey) return;
    setPreview(null);
    setError(null);
    let cancelled = false;
    nexiGlobalTemplateService.renderTemplatePreview(
      templateCall.template_name,
      templateCall.language_preference || 'zh_HK',
      templateCall.content_variables || null,
      clid || null
    )
      .then((res) => { if (!cancelled) setPreview(res); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load template'); });
    return () => { cancelled = true; };
  }, [templateKey, clid]);

  if (error) return <div className="text-sm text-red-300">{error}</div>;
  if (!preview) return <div className="text-sm text-gray-400">Loading template...</div>;

  const meta = preview.metadata || {};
  const quickReply = meta.quick_reply_options || [];
  const ctaOptions = meta.cta_url_options || [];
  const listOptions = meta.list_options;

  const language = templateCall.language_preference || 'zh_HK';
  const sections = listOptions?.sections?.[language] ?? listOptions?.sections?.zh_HK ?? listOptions?.sections?.en_US ?? [];
  const hasListSections = Array.isArray(sections) && sections.length > 0;

  return (
    <div className="space-y-2">
      <div className="text-sm whitespace-pre-wrap text-white">{preview.content || '(No content)'}</div>
      {quickReply.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-gray-400 mb-1">Quick reply</div>
          <div className="flex flex-wrap gap-2">
            {quickReply.map((opt, i) => {
              const label = typeof opt.label === 'string' ? opt.label : (opt.label?.en_US || opt.label?.['zh_HK'] || opt.value);
              const value = opt.value;
              return onQuickReplyClick ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => onQuickReplyClick(value)}
                  disabled={submitting}
                  className="px-2 py-1 text-xs bg-[#2A3942] text-white rounded hover:bg-[#3d4a52] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {label}
                </button>
              ) : (
                <span key={i} className="px-2 py-1 text-xs bg-[#2A3942] text-white rounded">{label}</span>
              );
            })}
          </div>
        </div>
      )}
      {ctaOptions.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-gray-400 mb-1">CTA</div>
          <div className="flex flex-wrap gap-2">
            {ctaOptions.map((opt, i) => (
              <a key={i} href={opt.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#53bdeb] underline">{opt.display_text || opt.url}</a>
            ))}
          </div>
        </div>
      )}
      {hasListSections && (
        <div className="mt-2 space-y-2">
          {(listOptions?.button_text?.[language] || listOptions?.header_text?.[language]) && (
            <div className="text-xs text-gray-400">
              {listOptions?.button_text?.[language] || listOptions?.header_text?.[language]}
            </div>
          )}
          {sections.map((section, sIdx) => (
            <div key={sIdx} className="space-y-1">
              {section?.title && (
                <div className="text-xs font-medium text-gray-400">{section.title}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {(section?.rows || []).map((row, rIdx) => {
                  const payload = row?.id ?? row?.title ?? '';
                  const label = row?.title ?? row?.id ?? '';
                  return onQuickReplyClick ? (
                    <button
                      key={rIdx}
                      type="button"
                      onClick={() => onQuickReplyClick(payload)}
                      disabled={submitting}
                      className="px-2 py-1 text-xs bg-[#2A3942] text-white rounded hover:bg-[#3d4a52] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {label}
                    </button>
                  ) : (
                    <span key={rIdx} className="px-2 py-1 text-xs bg-[#2A3942] text-white rounded">{label}</span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {listOptions && !hasListSections && (
        <div className="mt-2 text-xs text-gray-400">List template (see metadata)</div>
      )}
    </div>
  );
};

/**
 * A component for testing the agent API
 *
 * @param {Object} props
 * @param {Object} props.clinic - The clinic object
 */
const ApiTestSection = ({ clinic }) => {
  // API Test section states
  const [sessionId, setSessionId] = useState(() => {
    const savedSessionId = localStorage.getItem('agent_test_session_id');
    return savedSessionId || `test_${uuidv4()}`;
  });
  const [userMessage, setUserMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const chatEndRef = useRef(null);
  const [testConversationsList, setTestConversationsList] = useState([]);
  const [loadingTestList, setLoadingTestList] = useState(false);
  const [conversationSearch, setConversationSearch] = useState('');
  const [testPlans, setTestPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [isRunningPlan, setIsRunningPlan] = useState(false);
  const [rerunningCaseIndices, setRerunningCaseIndices] = useState([]); // case_index values currently being re-run (supports parallel re-runs)
  const [runProgressText, setRunProgressText] = useState('');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isRunReportModalOpen, setIsRunReportModalOpen] = useState(false);
  const [runReport, setRunReport] = useState(null);
  const [loadedReportMeta, setLoadedReportMeta] = useState(null); // { id, plan_name, ... } when viewing a persisted report
  const [runCaseIndices, setRunCaseIndices] = useState([]);
  const [isCasePickerOpen, setIsCasePickerOpen] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [loadingSavedReports, setLoadingSavedReports] = useState(false);
  const [pendingConversationCase, setPendingConversationCase] = useState(null);
  const [pendingConversationCases, setPendingConversationCases] = useState(null);
  const [pendingFocusPlanCase, setPendingFocusPlanCase] = useState(null);
  const [autoClosePlanModalOnSave, setAutoClosePlanModalOnSave] = useState(false);
  const [showCompareReports, setShowCompareReports] = useState(false);
  const runAbortRef = useRef(false);
  const planLoadSeqRef = useRef(0);
  const reportListSeqRef = useRef(0);
  // Authoritative, always-current run report used by concurrent re-runs to
  // merge their results without racing each other through React state timing.
  const latestRunReportRef = useRef(null);
  const rerunningIndicesRef = useRef(new Set());
  // Serialize PATCH persistence so overlapping parallel re-runs never clobber
  // each other (the final write always reflects every merged case).
  const rerunPersistInFlightRef = useRef(false);
  const rerunPersistPendingRef = useRef(false);

  // Persist session ID to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('agent_test_session_id', sessionId);
  }, [sessionId]);

  // Mirror the current run report into a ref so concurrent re-runs always merge
  // onto the latest snapshot regardless of React state batching.
  useEffect(() => {
    latestRunReportRef.current = runReport;
  }, [runReport]);

  const TEST_CUSTOMER_NUMBER = '+85200000000';

  const getConversationId = () => (clinic?.id && sessionId ? `test::${clinic.id}::${TEST_CUSTOMER_NUMBER}::testing::${sessionId}` : null);

  // Load previous messages for this conversation from ConversationMessage (source=testing)
  useEffect(() => {
    const conversationId = getConversationId();
    if (!conversationId) return;
    testingService.getTestAgentConversationMessages(conversationId).then((res) => {
      if (res.success && Array.isArray(res.messages)) setChatHistory(res.messages);
    }).catch(() => {});
  }, [sessionId, clinic?.id]);

  // Scroll to bottom of chat when history changes
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [chatHistory]);

  const loadTestAgentConversations = async () => {
    if (!clinic?.id) return;
    setLoadingTestList(true);
    try {
      const res = await testingService.getTestAgentConversations(clinic.id);
      if (res.success && Array.isArray(res.conversations)) {
        setTestConversationsList(res.conversations);
      }
    } catch (e) {
      console.error('Error loading test conversations:', e);
    } finally {
      setLoadingTestList(false);
    }
  };

  const selectTestConversation = (conversationId) => {
    const parts = conversationId.split('::');
    if (parts[0] === 'test' && parts.length >= 5) setSessionId(parts[4]);
    else if (parts.length >= 1) setSessionId(parts[parts.length - 1]);
    testingService.getTestAgentConversationMessages(conversationId).then((res) => {
      if (res.success && Array.isArray(res.messages)) setChatHistory(res.messages);
    }).catch(() => {});
  };

  useEffect(() => {
    loadTestAgentConversations();
  }, [clinic?.id]);

  // API Test section handlers
  const generateNewSessionId = () => {
    setSessionId(`test_${uuidv4()}`);
    setChatHistory([]);
  };

  const handleUserMessageChange = (e) => {
    setUserMessage(e.target.value);
  };

  const loadTestPlans = async () => {
    if (!clinic?.id) return;
    const requestSeq = planLoadSeqRef.current + 1;
    planLoadSeqRef.current = requestSeq;
    setLoadingPlans(true);
    try {
      const res = await Promise.race([
        testingService.listTestPlans(clinic.id),
        new Promise((_, reject) => {
          window.setTimeout(() => reject(new Error('Loading test plans timed out. Please refresh.')), 12000);
        }),
      ]);
      if (res.success && Array.isArray(res.plans)) {
        setTestPlans(res.plans);
        if (selectedPlanId && !res.plans.some((p) => p.id === selectedPlanId)) {
          setSelectedPlanId(null);
        }
      } else if (requestSeq === planLoadSeqRef.current) {
        setApiError(res?.error || 'Failed to load test plans.');
      }
    } catch (e) {
      if (requestSeq === planLoadSeqRef.current) {
        console.error('Error loading test plans:', e);
        setApiError(e?.message || 'Failed to load test plans.');
      }
    } finally {
      if (requestSeq === planLoadSeqRef.current) {
        setLoadingPlans(false);
      }
    }
  };

  useEffect(() => {
    loadTestPlans();
  }, [clinic?.id]);

  useEffect(() => {
    if (!clinic?.id) return;
    const payload = consumePendingTestPlanImport(clinic.id);
    if (!payload) return;
    setPendingConversationCases(payload.cases);
    if (payload.planId) setSelectedPlanId(payload.planId);
    setAutoClosePlanModalOnSave(false);
    setIsPlanModalOpen(true);
  }, [clinic?.id]);

  // When the active plan changes (or its case list resizes), reset the multi-select
  // to every non-skipped case so the default "Play" behaviour matches the previous PR.
  useEffect(() => {
    const selectedPlan = testPlans.find((p) => p.id === selectedPlanId);
    const cases = Array.isArray(selectedPlan?.cases) ? selectedPlan.cases : [];
    if (cases.length === 0) {
      if (runCaseIndices.length !== 0) setRunCaseIndices([]);
      return;
    }
    const defaultIndices = cases
      .map((c, idx) => (!c?.is_skipped ? idx : null))
      .filter((idx) => idx !== null);
    setRunCaseIndices(defaultIndices);
  }, [selectedPlanId, testPlans]); // eslint-disable-line react-hooks/exhaustive-deps

  const waitForAssistantReply = async (conversationId, initialCount = chatHistory.length) => {
    const pollIntervalMs = 1500;
    const maxAttempts = 40;
    let lastCount = initialCount;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (runAbortRef.current) return { success: false, error: 'Run cancelled by user.' };
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      const res = await testingService.getTestAgentConversationMessages(conversationId);
      if (!res.success || !Array.isArray(res.messages)) continue;
      const messages = res.messages;
      if (messages.length > lastCount && messages[messages.length - 1]?.role === 'assistant') {
        setChatHistory(messages);
        loadTestAgentConversations();
        return { success: true, messages, is_paused: !!res.is_paused };
      }
      // The router silently drops inbound predictions while the conversation
      // is paused (escalation / appointment confirmation), so no further
      // assistant reply will arrive. Bail out early instead of waiting the
      // full 60s timeout.
      if (res.is_paused) {
        setChatHistory(messages);
        loadTestAgentConversations();
        return { success: true, messages, is_paused: true, paused_without_reply: true };
      }
      lastCount = messages.length;
    }
    const res = await testingService.getTestAgentConversationMessages(conversationId);
    if (res.success && Array.isArray(res.messages)) setChatHistory(res.messages);
    loadTestAgentConversations();
    return {
      success: false,
      error: 'Agent reply did not appear in time.',
      is_paused: !!res?.is_paused,
    };
  };

  const sendMessageAndWaitReply = async (message, options = {}) => {
    const { appendUserMessage = true, conversationId: providedConversationId } = options;
    const trimmed = (message || '').trim();
    if (!trimmed) return { success: false, error: 'Message is empty.' };
    const conversationId = providedConversationId || getConversationId();
    if (!conversationId) return { success: false, error: 'Missing conversation ID.' };

    try {
      let initialCount = chatHistory.length;
      if (appendUserMessage) {
        setChatHistory((prev) => {
          initialCount = prev.length + 1;
          return [...prev, { role: 'user', content: trimmed }];
        });
      }
      const sendResult = await testingService.sendTestAgentMessage(conversationId, clinic.id, trimmed);
      if (!sendResult.success) {
        return { success: false, error: sendResult.error || 'Failed to send message' };
      }
      return await waitForAssistantReply(conversationId, initialCount);
    } catch (err) {
      return { success: false, error: err.message || 'Failed to send message' };
    }
  };

  const submitWithMessage = async (message) => {
    if (!message?.trim() || isSubmitting) return;
    setUserMessage('');
    setApiError(null);
    try {
      setIsSubmitting(true);
      const result = await sendMessageAndWaitReply(message, { appendUserMessage: true });
      if (!result.success) {
        setApiError(result.error || 'Failed to send message');
        setChatHistory(prev => [...prev, { role: 'system', content: `Error: ${result.error}` }]);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();

    if (!userMessage.trim() || isSubmitting) return;

    await submitWithMessage(userMessage.trim());
  };

  const handleSavePlan = async (planId, payload) => {
    const handler = planId ? testingService.updateTestPlan : testingService.createTestPlan;
    const result = planId ? await handler(planId, payload) : await handler(payload);
    if (result.success) {
      await loadTestPlans();
      if (result.plan?.id) setSelectedPlanId(result.plan.id);
      return { success: true };
    }
    setApiError(result.error || 'Failed to save test plan.');
    return { success: false, error: result.error };
  };

  const handleDeletePlan = async (planId) => {
    const result = await testingService.deleteTestPlan(planId);
    if (!result.success) {
      setApiError(result.error || 'Failed to delete test plan.');
      return;
    }
    if (selectedPlanId === planId) setSelectedPlanId(null);
    loadTestPlans();
  };

  const handleStopPlanRun = () => {
    runAbortRef.current = true;
    setRunProgressText('Stopping run...');
  };

  const buildCaseConversation = () => {
    const caseSessionId = `test_${uuidv4()}`;
    const caseConversationId = clinic?.id
      ? `test::${clinic.id}::${TEST_CUSTOMER_NUMBER}::testing::${caseSessionId}`
      : null;
    return { caseSessionId, caseConversationId };
  };

  const buildSkippedCaseReport = (caseItem, caseIdx) => ({
    case_index: caseIdx,
    name: caseItem?.name || `Case ${caseIdx + 1}`,
    session_id: null,
    conversation_id: null,
    exchanges: [],
    status: 'skipped',
    error: null,
  });

  const extractAssistantAnswer = (messages) => {
    const assistantReply = [...(messages || [])].reverse().find((m) => m.role === 'assistant');
    return (
      assistantReply?.content
      || (assistantReply?.is_reply_with_template ? '[Template reply]' : '[No assistant text reply]')
    );
  };

  // Send one customer turn on an isolated conversation and poll for the reply,
  // tracking that conversation's own message count. Unlike sendMessageAndWaitReply
  // this never touches the shared chat/session state, so many cases can run
  // concurrently without clobbering each other's visible chat.
  const sendCaseMessageAndWait = async (conversationId, text, prevCount) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return { success: true, messages: [], count: prevCount };
    const sendResult = await testingService.sendTestAgentMessage(conversationId, clinic.id, trimmed);
    if (!sendResult.success) {
      return { success: false, error: sendResult.error || 'Failed to send message' };
    }
    const pollIntervalMs = 1500;
    const maxAttempts = 40;
    let lastCount = prevCount;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (runAbortRef.current) return { success: false, error: 'Run cancelled by user.' };
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      const res = await testingService.getTestAgentConversationMessages(conversationId);
      if (!res.success || !Array.isArray(res.messages)) continue;
      const messages = res.messages;
      if (messages.length > lastCount && messages[messages.length - 1]?.role === 'assistant') {
        return { success: true, messages, is_paused: !!res.is_paused, count: messages.length };
      }
      if (res.is_paused) {
        return { success: true, messages, is_paused: true, paused_without_reply: true, count: messages.length };
      }
      lastCount = messages.length;
    }
    const res = await testingService.getTestAgentConversationMessages(conversationId);
    return {
      success: false,
      error: 'Agent reply did not appear in time.',
      is_paused: !!res?.is_paused,
      count: Array.isArray(res?.messages) ? res.messages.length : prevCount,
    };
  };

  // Core per-case assembly shared by every runner. `runMessage(text, msgIdx,
  // total, caseLabel)` performs one customer turn and returns
  // { success, answer?, is_paused?, error? }. Centralizing the exchange / pause /
  // failure handling keeps the sequential and parallel runners identical.
  const assembleCaseReport = async (caseItem, caseIdx, { sessionId: caseSessionId, conversationId, runMessage }) => {
    const caseLabel = caseItem?.name || `Case ${caseIdx + 1}`;
    const caseReport = {
      case_index: caseIdx,
      name: caseLabel,
      session_id: caseSessionId,
      conversation_id: conversationId,
      exchanges: [],
      status: 'running',
      error: null,
    };
    const messages = Array.isArray(caseItem.messages) ? caseItem.messages : [];
    let caseFailed = false;
    let casePaused = false;
    for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
      if (runAbortRef.current) throw new Error('Run cancelled by user.');
      const text = (messages[msgIdx] || '').trim();
      if (!text) continue;
      const result = await runMessage(text, msgIdx, messages.length, caseLabel);
      if (!result.success) {
        caseReport.status = 'failed';
        caseReport.error = `${caseLabel} message ${msgIdx + 1}: ${result.error}`;
        caseFailed = true;
        break;
      }
      caseReport.exchanges.push({ message: text, answer: result.answer });
      // Conversation paused (escalation / appointment confirmation created a
      // ConversationStatePause). The router silently drops further inbound
      // predictions until the pause expires, so stop iterating this case.
      if (result.is_paused) {
        const remaining = messages.length - (msgIdx + 1);
        const sentCount = msgIdx + 1;
        caseReport.note = remaining > 0
          ? `AI paused after message ${sentCount}/${messages.length}; remaining ${remaining} message${remaining === 1 ? '' : 's'} skipped.`
          : `AI paused after message ${sentCount}/${messages.length}.`;
        casePaused = true;
        break;
      }
    }
    if (!caseFailed) caseReport.status = 'completed';
    if (casePaused) caseReport.is_paused = true;
    return caseReport;
  };

  // Sequential runner used by the full plan "Play": mirrors progress into the
  // visible chat pane (setSessionId / setChatHistory) as each case runs.
  const runPlanCaseToReport = async (caseItem, caseIdx, { makeProgress } = {}) => {
    if (caseItem?.is_skipped) return buildSkippedCaseReport(caseItem, caseIdx);
    const { caseSessionId, caseConversationId } = buildCaseConversation();
    if (!caseConversationId) throw new Error('Missing conversation ID.');
    setSessionId(caseSessionId);
    setChatHistory([]);
    return assembleCaseReport(caseItem, caseIdx, {
      sessionId: caseSessionId,
      conversationId: caseConversationId,
      runMessage: async (text, msgIdx, total, caseLabel) => {
        if (makeProgress) setRunProgressText(makeProgress(msgIdx, total, caseLabel));
        let result = await sendMessageAndWaitReply(text, {
          appendUserMessage: true,
          conversationId: caseConversationId,
        });
        if (!result.success && !runAbortRef.current) {
          // Retry the same message once before failing the case.
          setRunProgressText(`Retrying ${caseLabel} message ${msgIdx + 1}/${total}`);
          result = await sendMessageAndWaitReply(text, {
            appendUserMessage: false,
            conversationId: caseConversationId,
          });
        }
        if (!result.success) return { success: false, error: result.error };
        return { success: true, answer: extractAssistantAnswer(result.messages), is_paused: result.is_paused };
      },
    });
  };

  // Parallel-safe runner used by the report-modal re-run: a fully isolated
  // conversation with local message-count tracking. It never touches the shared
  // chat/session state, so several cases can be re-run concurrently.
  const runReportCaseParallel = async (caseItem, caseIdx) => {
    if (caseItem?.is_skipped) return buildSkippedCaseReport(caseItem, caseIdx);
    const { caseSessionId, caseConversationId } = buildCaseConversation();
    if (!caseConversationId) throw new Error('Missing conversation ID.');
    let convCount = 0;
    return assembleCaseReport(caseItem, caseIdx, {
      sessionId: caseSessionId,
      conversationId: caseConversationId,
      runMessage: async (text) => {
        let result = await sendCaseMessageAndWait(caseConversationId, text, convCount);
        if (!result.success && !runAbortRef.current) {
          // Retry the same message once before failing the case.
          result = await sendCaseMessageAndWait(caseConversationId, text, convCount);
        }
        if (!result.success) return { success: false, error: result.error };
        if (Number.isInteger(result.count)) convCount = result.count;
        return { success: true, answer: extractAssistantAnswer(result.messages), is_paused: result.is_paused };
      },
    });
  };

  const handleRunPlan = async (overrideIndices = null) => {
    const selectedPlan = testPlans.find((p) => p.id === selectedPlanId);
    if (!selectedPlan) {
      setApiError('Please select a plan to run.');
      return;
    }
    const planCases = Array.isArray(selectedPlan.cases) ? selectedPlan.cases : [];
    if (planCases.length === 0) {
      setApiError('Selected plan has no test cases.');
      return;
    }

    // Normalize / de-duplicate the requested indices and keep them sorted to preserve order.
    const requested = Array.isArray(overrideIndices) ? overrideIndices : runCaseIndices;
    const indices = Array.from(new Set(requested || []))
      .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < planCases.length)
      .sort((a, b) => a - b);

    if (indices.length === 0) {
      setApiError('Pick at least one case to run.');
      return;
    }

    runAbortRef.current = false;
    setApiError(null);
    setIsRunningPlan(true);
    setIsSubmitting(true);
    setLoadedReportMeta(null);
    setRunReport(null);
    const nextReport = {
      plan_id: selectedPlan.id,
      plan_name: selectedPlan.name || 'Plan',
      clid: clinic?.id || null,
      started_at: new Date().toISOString(),
      finished_at: null,
      status: 'running',
      selected_case_indices: indices,
      cases: [],
    };
    // Persist the just-finished run automatically. Skipped if the run has
    // zero cases (e.g. cancelled before the first case finished) or no clinic
    // is available. The modal is already open at this point — we only need to
    // promote the in-memory report to a persisted one so it shows up in the
    // Past reports dropdown without the user having to click Save.
    const autoSaveRunReport = async (reportObj, progressPrefix) => {
      if (!reportObj || !clinic?.id) return;
      const hasCases = Array.isArray(reportObj.cases) && reportObj.cases.length > 0;
      if (!hasCases) return;
      const payload = computeRunReportPayload(reportObj, {
        clinicId: clinic.id,
        planId: selectedPlan.id,
      });
      if (!payload) return;
      try {
        const saveResult = await testingService.saveRunReport(payload);
        if (saveResult?.success && saveResult?.id) {
          setLoadedReportMeta({
            id: saveResult.id,
            plan_id: payload.plan_id,
            plan_name: payload.plan_name,
            status: payload.status,
            started_at: payload.started_at,
          });
          loadSavedReports();
          if (progressPrefix) {
            setRunProgressText(`${progressPrefix} Report auto-saved.`);
          }
        } else {
          console.error('Auto-save of run report failed:', saveResult?.error);
          if (progressPrefix) {
            setRunProgressText(`${progressPrefix} (Auto-save failed — click "Save report".)`);
          }
        }
      } catch (saveErr) {
        console.error('Auto-save of run report threw:', saveErr);
        if (progressPrefix) {
          setRunProgressText(`${progressPrefix} (Auto-save failed — click "Save report".)`);
        }
      }
    };
    let failedCount = 0;
    try {
      for (let i = 0; i < indices.length; i++) {
        if (runAbortRef.current) throw new Error('Run cancelled by user.');
        const caseIdx = indices[i];
        const caseItem = planCases[caseIdx];
        const caseReport = await runPlanCaseToReport(caseItem, caseIdx, {
          makeProgress: (msgIdx, total, label) =>
            `Running ${label} (${msgIdx + 1}/${total}) — case ${i + 1}/${indices.length}`,
        });
        if (caseReport.status === 'failed') failedCount += 1;
        nextReport.cases.push(caseReport);
      }
      const ran = nextReport.cases.length;
      const summary = failedCount > 0
        ? `Plan run finished with ${failedCount} failed case${failedCount === 1 ? '' : 's'} (of ${ran}).`
        : `Plan run completed (${ran} case${ran === 1 ? '' : 's'}).`;
      setRunProgressText(summary);
      nextReport.status = failedCount > 0 ? 'completed_with_failures' : 'completed';
      nextReport.finished_at = new Date().toISOString();
      setRunReport(nextReport);
      setIsRunReportModalOpen(true);
      await autoSaveRunReport(nextReport, summary);
    } catch (err) {
      setApiError(err.message || 'Plan run failed.');
      const baseProgress = err.message?.startsWith('Run cancelled') ? 'Run cancelled.' : 'Run failed.';
      setRunProgressText(baseProgress);
      nextReport.finished_at = new Date().toISOString();
      if (nextReport.status === 'running') {
        nextReport.status = err.message?.startsWith('Run cancelled') ? 'cancelled' : 'failed';
      }
      setRunReport(nextReport);
      setIsRunReportModalOpen(true);
      await autoSaveRunReport(nextReport, baseProgress);
    } finally {
      setIsRunningPlan(false);
      setIsSubmitting(false);
      runAbortRef.current = false;
      loadTestAgentConversations();
    }
  };

  const loadSavedReports = useCallback(async () => {
    if (!clinic?.id) {
      setSavedReports([]);
      return;
    }
    const seq = reportListSeqRef.current + 1;
    reportListSeqRef.current = seq;
    setLoadingSavedReports(true);
    try {
      const res = await testingService.listRunReports(clinic.id, { limit: 50 });
      if (seq !== reportListSeqRef.current) return;
      if (res.success && Array.isArray(res.reports)) {
        setSavedReports(res.reports);
      } else if (res.error) {
        console.error('Failed to load saved run reports:', res.error);
      }
    } catch (e) {
      if (seq === reportListSeqRef.current) {
        console.error('Error loading saved run reports:', e);
      }
    } finally {
      if (seq === reportListSeqRef.current) {
        setLoadingSavedReports(false);
      }
    }
  }, [clinic?.id]);

  useEffect(() => {
    loadSavedReports();
  }, [loadSavedReports]);

  const buildRunReportPayload = useCallback(
    ({ notesOverride, nameOverride } = {}) =>
      computeRunReportPayload(runReport, {
        clinicId: clinic?.id,
        planId: selectedPlanId,
        notesOverride,
        nameOverride,
      }),
    [runReport, selectedPlanId, clinic?.id]
  );

  const handleSaveCurrentRunReport = useCallback(
    async (overrides = {}) => {
      const notesOverride = typeof overrides === 'string' ? overrides : overrides?.notes;
      const nameOverride = typeof overrides === 'object' && overrides ? overrides.name : undefined;
      const payload = buildRunReportPayload({ notesOverride, nameOverride });
      if (!payload) return { success: false, error: 'No report to save.' };
      const result = await testingService.saveRunReport(payload);
      if (result?.success && result?.id) {
        setRunReport(payload.report);
        setLoadedReportMeta({
          id: result.id,
          plan_id: payload.plan_id,
          plan_name: payload.plan_name,
          status: payload.status,
          started_at: payload.started_at,
        });
        loadSavedReports();
      }
      return result;
    },
    [buildRunReportPayload, loadSavedReports]
  );

  const handleSelectSavedReport = useCallback(
    async (reportId) => {
      if (!reportId) return;
      const res = await testingService.getRunReport(reportId);
      if (!res?.success || !res?.report) {
        setApiError(res?.error || 'Failed to load saved report.');
        return;
      }
      const detail = res.report;
      const persistedReport = {
        ...(detail.report || {}),
        plan_id: detail.plan_id || (detail.report || {}).plan_id || null,
        plan_name: detail.plan_name || (detail.report || {}).plan_name || 'Plan',
        clid: detail.clid || (detail.report || {}).clid || null,
        status: detail.status || (detail.report || {}).status || 'completed',
        started_at: detail.started_at || (detail.report || {}).started_at || null,
        finished_at: detail.finished_at || (detail.report || {}).finished_at || null,
        selected_case_indices: detail.selected_case_indices || (detail.report || {}).selected_case_indices || [],
        case_total: detail.case_total,
        case_completed: detail.case_completed,
        case_failed: detail.case_failed,
        case_skipped: detail.case_skipped,
        notes: detail.notes || '',
      };
      setRunReport(persistedReport);
      setLoadedReportMeta({
        id: detail.id,
        plan_id: detail.plan_id,
        plan_name: detail.plan_name,
        status: detail.status,
        started_at: detail.started_at,
      });
      setIsRunReportModalOpen(true);
    },
    []
  );

  const handleDeleteSavedReport = useCallback(
    async (reportId) => {
      if (!reportId) return { success: false, error: 'Missing report id.' };
      const res = await testingService.deleteRunReport(reportId);
      if (res?.success) {
        if (loadedReportMeta?.id === reportId) {
          setLoadedReportMeta(null);
          setRunReport(null);
          setIsRunReportModalOpen(false);
        }
        loadSavedReports();
      }
      return res;
    },
    [loadedReportMeta?.id, loadSavedReports]
  );

  const handleUpdateRunReportNotes = useCallback(
    async (reportId, notes) => {
      if (!reportId) return { success: false, error: 'Missing report id.' };
      const res = await testingService.updateRunReportNotes(reportId, notes);
      if (res?.success) {
        setRunReport((prev) => (prev ? { ...prev, notes: notes || '' } : prev));
        setSavedReports((prev) =>
          prev.map((row) =>
            row.id === reportId
              ? {
                  ...row,
                  notes_preview: (notes || '').length > 200 ? `${(notes || '').slice(0, 197)}...` : notes || '',
                }
              : row
          )
        );
      }
      return res;
    },
    []
  );

  const handleUpdateRunReportName = useCallback(async (reportId, name) => {
    const trimmed = (name || '').trim() || 'Plan';
    let reportSnapshot = null;
    setRunReport((prev) => {
      if (!prev) return prev;
      reportSnapshot = { ...prev, plan_name: trimmed };
      return reportSnapshot;
    });
    setLoadedReportMeta((prev) => (prev ? { ...prev, plan_name: trimmed } : prev));
    if (!reportId) return { success: true };
    const res = await testingService.updateRunReport(reportId, {
      plan_name: trimmed,
      report: reportSnapshot || { plan_name: trimmed },
    });
    if (res?.success) {
      setSavedReports((prev) =>
        prev.map((row) => (row.id === reportId ? { ...row, plan_name: trimmed } : row))
      );
    }
    return res;
  }, []);

  const handleOpenReportConversation = useCallback((targetSessionId) => {
    if (!targetSessionId) return;
    setSessionId(targetSessionId);
    setIsRunReportModalOpen(false);
  }, []);

  const handleEditReportCaseInPlan = useCallback(
    async (reportCase) => {
      const currentReport = latestRunReportRef.current || runReport;
      const planId = currentReport?.plan_id;
      if (!planId) {
        return { success: false, error: 'This report is not linked to a test plan.' };
      }
      let plan = testPlans.find((p) => p.id === planId);
      let planCases = Array.isArray(plan?.cases) ? plan.cases : [];
      if (!plan) {
        try {
          const res = await testingService.listTestPlans(clinic?.id);
          if (res?.success && Array.isArray(res.plans)) {
            setTestPlans(res.plans);
            plan = res.plans.find((p) => p.id === planId);
            planCases = Array.isArray(plan?.cases) ? plan.cases : [];
          }
        } catch (err) {
          return { success: false, error: err?.message || 'Failed to load test plans.' };
        }
      }
      if (!plan) {
        return { success: false, error: 'Test plan for this report was not found.' };
      }
      const caseIndex = resolvePlanCaseIndex(planCases, reportCase);
      if (caseIndex < 0) {
        return { success: false, error: 'Could not find this case in the test plan.' };
      }
      setSelectedPlanId(planId);
      setPendingFocusPlanCase({
        caseIndex,
        caseName: reportCase?.name || planCases[caseIndex]?.name || null,
      });
      setIsRunReportModalOpen(false);
      setIsPlanModalOpen(true);
      return { success: true };
    },
    [clinic?.id, runReport, testPlans]
  );

  const handleMutateReportExchange = useCallback(
    async (caseItem, exchangeIndex, mutation) => {
      const currentReport = latestRunReportRef.current;
      if (!currentReport) return { success: false, error: 'No report loaded.' };
      const nextReport = mutateReportExchanges(currentReport, caseItem, exchangeIndex, mutation);
      setRunReport(nextReport);
      const reportId = loadedReportMeta?.id;
      if (!reportId) return { success: true };
      try {
        const res = await testingService.updateRunReport(reportId, { report: nextReport });
        if (!res?.success) {
          return { success: false, error: res?.error || 'Failed to save exchange changes.' };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err?.message || 'Failed to save exchange changes.' };
      }
    },
    [loadedReportMeta?.id]
  );

  const handleSetReportCaseReviewed = useCallback(
    async (caseItem, reviewed) => {
      const currentReport = latestRunReportRef.current;
      if (!currentReport) return { success: false, error: 'No report loaded.' };
      const nextReport = setReportCaseReviewed(currentReport, caseItem, reviewed, matchReportCase);
      setRunReport(nextReport);
      const reportId = loadedReportMeta?.id;
      if (!reportId) return { success: true };
      try {
        const res = await testingService.updateRunReport(reportId, { report: nextReport });
        if (!res?.success) {
          return { success: false, error: res?.error || 'Failed to save review status.' };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err?.message || 'Failed to save review status.' };
      }
    },
    [loadedReportMeta?.id]
  );

  const handleDeleteReportCase = useCallback(
    async (caseItem) => {
      const currentReport = latestRunReportRef.current;
      if (!currentReport) return { success: false, error: 'No report loaded.' };
      const nextReport = removeReportCase(currentReport, caseItem);
      setRunReport(nextReport);
      const reportId = loadedReportMeta?.id;
      if (!reportId) return { success: true };
      try {
        const res = await testingService.updateRunReport(reportId, {
          status: nextReport.status,
          finished_at: nextReport.finished_at,
          case_total: nextReport.case_total,
          case_completed: nextReport.case_completed,
          case_failed: nextReport.case_failed,
          case_skipped: nextReport.case_skipped,
          report: nextReport,
        });
        if (!res?.success) {
          return { success: false, error: res?.error || 'Failed to delete test case.' };
        }
        return { success: true };
      } catch (err) {
        return { success: false, error: err?.message || 'Failed to delete test case.' };
      }
    },
    [loadedReportMeta?.id]
  );

  // Max number of cases re-run concurrently — keeps the agent backend and the
  // browser's polling load bounded while still running "in parallel".
  const MAX_PARALLEL_RERUN = 5;

  // Run `worker(item)` over items with a bounded number of concurrent runners.
  const runWithConcurrency = async (items, limit, worker) => {
    const results = new Array(items.length);
    let cursor = 0;
    const launchCount = Math.min(Math.max(1, limit), items.length);
    const runners = Array.from({ length: launchCount }, async () => {
      while (true) {
        const idx = cursor;
        cursor += 1;
        if (idx >= items.length) break;
        results[idx] = await worker(items[idx], idx);
      }
    });
    await Promise.all(runners);
    return results;
  };

  // Persist the latest merged re-run report. Serialized (with a single pending
  // flag) so overlapping parallel re-runs coalesce into ordered PATCHes and the
  // final write always reflects every merged case.
  const persistRerunReport = (reportId) => {
    if (!reportId) return;
    if (rerunPersistInFlightRef.current) {
      rerunPersistPendingRef.current = true;
      return;
    }
    rerunPersistInFlightRef.current = true;
    (async () => {
      try {
        do {
          rerunPersistPendingRef.current = false;
          const report = latestRunReportRef.current;
          if (!report) break;
          const counts = summarizeRunReportCases(report.cases);
          await testingService.updateRunReport(reportId, {
            status: report.status,
            finished_at: report.finished_at,
            case_total: counts.total,
            case_completed: counts.completed,
            case_failed: counts.failed,
            case_skipped: counts.skipped,
            report,
          });
        } while (rerunPersistPendingRef.current);
        loadSavedReports();
      } catch (err) {
        console.error('Failed to persist re-run report update:', err);
      } finally {
        rerunPersistInFlightRef.current = false;
      }
    })();
  };

  // Re-run one or many cases from the open run report. Cases execute in parallel
  // (bounded concurrency); each result is spliced back into the report the moment
  // it finishes (in-memory + persisted, if the report is saved). Concurrent calls
  // and already-running cases are de-duplicated via `rerunningIndicesRef`.
  const handleRerunReportCases = async (reportCases, options = {}) => {
    const messageSource = options?.messageSource === RERUN_MESSAGE_SOURCE.PLAN
      ? RERUN_MESSAGE_SOURCE.PLAN
      : RERUN_MESSAGE_SOURCE.REPORT;
    const refreshPlan = messageSource === RERUN_MESSAGE_SOURCE.PLAN || options?.refreshPlan === true;
    const requested = (Array.isArray(reportCases) ? reportCases : [reportCases]).filter(Boolean);
    if (requested.length === 0) return { success: false, error: 'No case to re-run.' };
    if (isRunningPlan) return { success: false, error: 'A plan run is already in progress.' };
    if (!clinic?.id) return { success: false, error: 'Missing clinic.' };
    const currentReport = latestRunReportRef.current || runReport;
    if (!currentReport) return { success: false, error: 'No report loaded.' };

    let planCases = [];
    if (refreshPlan) {
      try {
        const res = await testingService.listTestPlans(clinic.id);
        if (!res?.success || !Array.isArray(res.plans)) {
          return { success: false, error: res?.error || 'Failed to load test plans.' };
        }
        setTestPlans(res.plans);
        const plan = res.plans.find((p) => p.id === currentReport.plan_id);
        planCases = Array.isArray(plan?.cases) ? plan.cases : [];
        if (!plan) {
          return { success: false, error: 'Test plan for this report was not found.' };
        }
      } catch (err) {
        return { success: false, error: err?.message || 'Failed to load test plans.' };
      }
    } else {
      const plan = testPlans.find((p) => p.id === currentReport.plan_id);
      planCases = Array.isArray(plan?.cases) ? plan.cases : [];
    }

    // Resolve each requested row to a live plan case (prefer matching by name
    // since the plan may have been edited since the run), skipping any case that
    // is already re-running.
    const resolved = [];
    requested.forEach((reportCase) => {
      let planCaseIdx = -1;
      if (reportCase.name) planCaseIdx = planCases.findIndex((c) => c?.name === reportCase.name);
      if (
        planCaseIdx < 0 &&
        Number.isInteger(reportCase.case_index) &&
        reportCase.case_index >= 0 &&
        reportCase.case_index < planCases.length
      ) {
        planCaseIdx = reportCase.case_index;
      }
      if (planCaseIdx < 0) return;
      const matchIndex = Number.isInteger(reportCase.case_index) ? reportCase.case_index : planCaseIdx;
      if (rerunningIndicesRef.current.has(matchIndex)) return;
      const planCase = planCases[planCaseIdx];
      const effectivePlanCase =
        messageSource === RERUN_MESSAGE_SOURCE.PLAN
          ? planCase
          : buildReportDrivenPlanCase(reportCase, planCase);
      if (!effectivePlanCase) return;
      resolved.push({
        planCase: effectivePlanCase,
        planCaseIdx,
        matchIndex,
        name: reportCase.name,
        messageSource,
      });
    });

    if (resolved.length === 0) {
      return {
        success: false,
        error: 'Nothing to re-run — selected case(s) are already running or were not found in the plan.',
      };
    }

    const reportId = loadedReportMeta?.id || null;
    runAbortRef.current = false;
    setApiError(null);
    resolved.forEach((r) => rerunningIndicesRef.current.add(r.matchIndex));
    setRerunningCaseIndices((prev) => Array.from(new Set([...prev, ...resolved.map((r) => r.matchIndex)])));

    const sourceLabel =
      messageSource === RERUN_MESSAGE_SOURCE.PLAN ? 'latest plan messages' : 'report messages';

    // Splice a finished case back into the report, recomputing rolled-up counts.
    const mergeCaseResult = (matchIndex, name, newCaseReport) => {
      setRunReport((prev) => {
        if (!prev) return prev;
        const updatedCases = (prev.cases || []).map((c) => {
          const sameByIndex = Number.isInteger(matchIndex) && c?.case_index === matchIndex;
          const sameByName = !Number.isInteger(matchIndex) && name && c?.name === name;
          return sameByIndex || sameByName ? newCaseReport : c;
        });
        const counts = summarizeRunReportCases(updatedCases);
        const updated = {
          ...prev,
          cases: updatedCases,
          status: counts.failed > 0 ? 'completed_with_failures' : 'completed',
          finished_at: new Date().toISOString(),
          case_total: counts.total,
          case_completed: counts.completed,
          case_failed: counts.failed,
          case_skipped: counts.skipped,
        };
        latestRunReportRef.current = updated;
        return updated;
      });
    };

    setRunProgressText(`Re-running ${resolved.length} case${resolved.length === 1 ? '' : 's'} (${sourceLabel})…`);
    try {
      const results = await runWithConcurrency(resolved, MAX_PARALLEL_RERUN, async (item) => {
        const caseLabel = item.planCase?.name || `Case ${item.planCaseIdx + 1}`;
        try {
          const newCaseReport = await runReportCaseParallel(item.planCase, item.planCaseIdx);
          // Keep the report row's original case_index so its identity stays
          // stable even when the plan was reordered and we matched by name.
          newCaseReport.case_index = item.matchIndex;
          mergeCaseResult(item.matchIndex, item.name, newCaseReport);
          persistRerunReport(reportId);
          return { success: true, status: newCaseReport.status, label: caseLabel };
        } catch (err) {
          return { success: false, error: err?.message || 'Failed to re-run case.', label: caseLabel };
        } finally {
          rerunningIndicesRef.current.delete(item.matchIndex);
          setRerunningCaseIndices((prev) => prev.filter((i) => i !== item.matchIndex));
        }
      });
      const failures = results.filter((r) => r && r.success === false);
      const ranOk = results.length - failures.length;
      const message = failures.length === 0
        ? `Re-ran ${ranOk} case${ranOk === 1 ? '' : 's'} using ${sourceLabel}.`
        : `Re-ran ${ranOk} case${ranOk === 1 ? '' : 's'} using ${sourceLabel}; ${failures.length} could not run.`;
      setRunProgressText(message);
      return { success: true, message };
    } finally {
      runAbortRef.current = false;
      loadTestAgentConversations();
    }
  };

  const handleConfirmCasePicker = useCallback(
    (indices) => {
      setRunCaseIndices(indices || []);
      setIsCasePickerOpen(false);
      if (indices && indices.length > 0) {
        handleRunPlan(indices);
      }
    },
    [handleRunPlan]
  );

  const handleOpenReportsModal = useCallback(async () => {
    setIsRunReportModalOpen(true);
    if (runReport) return;
    // No in-memory report — try the most-recently saved one for this clinic.
    if (!savedReports || savedReports.length === 0) {
      await loadSavedReports();
      return;
    }
    const newest = savedReports[0];
    if (newest?.id) {
      await handleSelectSavedReport(newest.id);
    }
  }, [runReport, savedReports, loadSavedReports, handleSelectSavedReport]);

  // Open the report modal for an "expected answers" report built in the plan
  // editor (no agent run). In-memory only — not auto-saved.
  const handleGenerateExpectedReport = useCallback((report) => {
    if (!report) return;
    setLoadedReportMeta(null);
    setRunReport(report);
    setIsRunReportModalOpen(true);
  }, []);

  const lastRunFailedIndices = useMemo(() => {
    const cases = Array.isArray(runReport?.cases) ? runReport.cases : [];
    const set = new Set();
    cases.forEach((c) => {
      if (c?.status === 'failed' && Number.isInteger(c?.case_index)) {
        set.add(c.case_index);
      }
    });
    return set;
  }, [runReport]);

  const generateUniqueCaseCode = (clid, cases) => {
    const prefix = String(clid || 'CASE');
    const sourceCases = Array.isArray(cases) ? cases : [];
    const usedNumbers = new Set();
    sourceCases.forEach((caseItem) => {
      const caseName = String(caseItem?.name || '');
      if (!caseName.startsWith(`${prefix}.`)) return;
      const suffix = caseName.slice(prefix.length + 1);
      if (!/^\d+$/.test(suffix)) return;
      usedNumbers.add(parseInt(suffix, 10));
    });
    let nextNumber = 1;
    while (usedNumbers.has(nextNumber)) nextNumber += 1;
    return `${prefix}.${String(nextNumber).padStart(4, '0')}`;
  };

  const handleAddConversationToPlan = () => {
    const conversationMessages = Array.isArray(chatHistory) ? chatHistory : [];
    if (conversationMessages.length === 0) {
      setApiError('No conversation messages available to add.');
      return;
    }

    const pendingUserMessages = [];
    const pairs = [];
    const userMessages = [];

    conversationMessages.forEach((message) => {
      const role = String(message?.role || '').toLowerCase();
      const content = String(message?.content || '').trim();
      if (!content) return;
      if (role === 'user') {
        pendingUserMessages.push(content);
        userMessages.push(content);
        return;
      }
      if (role === 'assistant' && pendingUserMessages.length > 0) {
        const clientMessage = pendingUserMessages.shift();
        pairs.push({
          client_message: clientMessage,
          expected_result: content,
        });
      }
    });

    if (userMessages.length === 0) {
      setApiError('No user messages available to add.');
      return;
    }

    const sourceCases = Array.isArray(selectedPlan?.cases) ? selectedPlan.cases : [];
    setPendingConversationCase({
      name: generateUniqueCaseCode(clinic?.id, sourceCases),
      messages: userMessages,
      preview_pairs: pairs,
      annotation: '',
      is_skipped: false,
    });
    setApiError(null);
    setAutoClosePlanModalOnSave(true);
    setIsPlanModalOpen(true);
  };

  const handleClosePlanModal = () => {
    setIsPlanModalOpen(false);
    setPendingConversationCase(null);
    setAutoClosePlanModalOnSave(false);
  };

  // Handle quick reply (interrupt): Confirm or Reject — send via router, then poll for reply
  const handleInterruptAction = async (assistantMessage, approved) => {
    if (!assistantMessage?.interrupted_tool_calls?.length || isSubmitting) return;

    const conversationId = getConversationId();
    if (!conversationId) return;
    const text = approved ? 'Confirm' : 'Reject';
    const userEntry = { role: 'user', content: approved ? 'Confirmed' : 'Rejected' };
    setChatHistory(prev => [...prev, userEntry]);
    setUserMessage('');
    setApiError(null);

    try {
      setIsSubmitting(true);
      const sendResult = await testingService.sendTestAgentMessage(conversationId, clinic.id, text);
      if (!sendResult.success) {
        setApiError(sendResult.error || 'Failed to send');
        setChatHistory(prev => [...prev.slice(0, -1), { role: 'system', content: `Error: ${sendResult.error}` }]);
        return;
      }
      const pollIntervalMs = 1500;
      const maxAttempts = 40;
      let lastCount = 0;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((r) => setTimeout(r, pollIntervalMs));
        const res = await testingService.getTestAgentConversationMessages(conversationId);
        if (!res.success || !Array.isArray(res.messages)) continue;
        const messages = res.messages;
        if (messages.length > lastCount && messages[messages.length - 1]?.role === 'assistant') {
          setChatHistory(messages);
          loadTestAgentConversations();
          return;
        }
        lastCount = messages.length;
      }
      setApiError('Agent reply did not appear in time.');
      const res = await testingService.getTestAgentConversationMessages(conversationId);
      if (res.success && Array.isArray(res.messages)) setChatHistory(res.messages);
      loadTestAgentConversations();
    } catch (err) {
      console.error('Error on interrupt action:', err);
      setApiError(err.message || 'Failed to send');
      setChatHistory(prev => [...prev, { role: 'system', content: `Error: ${err.message}` }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // WhatsApp-style timestamp for bubbles
  const formatBubbleTime = () => {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getConversationShortName = (conversationId) => {
    if (!conversationId) return '';
    return conversationId.startsWith('test::') ? (conversationId.split('::').pop() || conversationId) : (conversationId.split('::')[0] || conversationId);
  };

  const formatConversationTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (msgDate.getTime() === today.getTime()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (msgDate.getTime() === yesterday.getTime()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const isConversationSelected = (conversationId) => {
    const short = getConversationShortName(conversationId);
    return short === sessionId;
  };

  const filteredConversations = conversationSearch.trim()
    ? testConversationsList.filter((c) => getConversationShortName(c.conversation_id).toLowerCase().includes(conversationSearch.trim().toLowerCase()))
    : testConversationsList;

  const selectedPlan = useMemo(
    () => (selectedPlanId ? testPlans.find((p) => p.id === selectedPlanId) : null),
    [testPlans, selectedPlanId]
  );

  const selectedPlanSummary = useMemo(() => {
    const cases = Array.isArray(selectedPlan?.cases) ? selectedPlan.cases : [];
    const codeCount = cases.length;
    let messageCount = 0;
    let skippedCount = 0;
    cases.forEach((c) => {
      if (c?.is_skipped) skippedCount += 1;
      if (Array.isArray(c?.messages)) {
        messageCount += c.messages.filter((m) => String(m || '').trim()).length;
      }
    });
    return { codeCount, messageCount, skippedCount };
  }, [selectedPlan]);

  return (
      <div className="flex h-[clamp(560px,72vh,860px)] min-h-0 rounded-xl overflow-hidden shadow-lg border border-gray-200 w-full bg-white">
        {/* Left: Test cases + conversation list */}
        <div className="w-80 flex-shrink-0 flex flex-col min-h-0 border-r border-gray-200 bg-[#F0F2F5]">
          <div className="flex-shrink-0 p-3 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Test cases</h4>
            </div>
            <select
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none"
              value={selectedPlanId || ''}
              onChange={(e) => setSelectedPlanId(e.target.value || null)}
              disabled={loadingPlans || isRunningPlan}
            >
              <option value="">{loadingPlans ? 'Loading plans…' : 'Select a test plan'}</option>
              {testPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
            <div className="mt-2 rounded-lg border border-gray-100 bg-gray-50/80 p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setAutoClosePlanModalOnSave(false);
                    setIsPlanModalOpen(true);
                  }}
                  disabled={loadingPlans || isRunningPlan}
                  className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50"
                >
                  Add / Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleRunPlan()}
                  disabled={!selectedPlanId || isRunningPlan || loadingPlans || runCaseIndices.length === 0 || rerunningCaseIndices.length > 0}
                  className="px-3 py-1.5 text-xs rounded bg-[#00A884] text-white hover:bg-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  title={runCaseIndices.length === 0 ? 'Pick at least one case to run.' : `Run ${runCaseIndices.length} selected case${runCaseIndices.length === 1 ? '' : 's'}.`}
                >
                  <PlayIcon className="h-3.5 w-3.5" aria-hidden />
                  Play
                </button>
                <button
                  type="button"
                  onClick={handleOpenReportsModal}
                  disabled={!runReport && (loadingSavedReports || savedReports.length === 0)}
                  className="px-3 py-1.5 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Open the run report modal (current run or most-recent saved)."
                >
                  Reports
                </button>
              </div>
              <div className="mt-2 rounded-lg border border-gray-200 bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCompareReports((prev) => !prev)}
                  disabled={isRunningPlan}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span aria-hidden>{showCompareReports ? '▾' : '▸'}</span>
                  Compare run reports (AI)
                </button>
                {showCompareReports && (
                  <div className="px-2 pb-2 pt-1 border-t border-gray-100">
                    <RunReportComparePanel
                      clinicId={clinic?.id}
                      planId={selectedPlanId}
                      isRunningPlan={isRunningPlan}
                      theme="light"
                    />
                  </div>
                )}
              </div>
              {selectedPlanId && selectedPlanSummary.codeCount > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsCasePickerOpen(true)}
                    disabled={isRunningPlan}
                    className="flex-1 min-w-0 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-left truncate"
                    title="Pick which cases will run in the next Play."
                  >
                    Choose cases ({runCaseIndices.length}/{selectedPlanSummary.codeCount})
                  </button>
                </div>
              )}
              {!selectedPlanId ? (
                <div className="mt-2 text-xs text-gray-500">
                  Select a plan, then Edit to review or Play to run.
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-600">
                  <span className="px-2 py-1 rounded bg-white border border-gray-200">
                    Conversation: {selectedPlanSummary.codeCount}
                  </span>
                  <span className="px-2 py-1 rounded bg-white border border-gray-200">
                    Messages: {selectedPlanSummary.messageCount}
                  </span>
                  <span className="px-2 py-1 rounded bg-white border border-gray-200">
                    Skipped: {selectedPlanSummary.skippedCount}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="p-3 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-800">對話</h4>
              <button
                type="button"
                onClick={generateNewSessionId}
                className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
                title="New conversation"
                aria-label="New conversation"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m14.356 2A8.001 8.001 0 006.582 9M20 20v-5h-.581m0 0A8.003 8.003 0 015.64 15" />
                </svg>
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={conversationSearch}
                onChange={(e) => setConversationSearch(e.target.value)}
                placeholder="搜尋對話"
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-100 border-0 text-sm placeholder-gray-500 focus:ring-2 focus:ring-[#00A884]/30 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {loadingTestList ? (
              <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {conversationSearch.trim() ? 'No matching conversations.' : 'No conversations yet. Send a message to start.'}
              </div>
            ) : (
              <ul className="py-1">
                {filteredConversations.map((c) => {
                  const short = getConversationShortName(c.conversation_id);
                  const selected = isConversationSelected(c.conversation_id);
                  return (
                    <li key={c.conversation_id}>
                      <button
                        type="button"
                        onClick={() => selectTestConversation(c.conversation_id)}
                        className={`w-full flex items-start gap-3 px-3 py-3 text-left hover:bg-gray-200/80 ${selected ? 'bg-[#E7F5EC]' : ''}`}
                      >
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00A884] flex items-center justify-center text-white text-sm font-medium">
                          {short.slice(0, 2).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">{short}</div>
                          <div className="text-xs text-gray-500 truncate">{c.message_count} message{c.message_count !== 1 ? 's' : ''}</div>
                        </div>
                        <span className="flex-shrink-0 text-xs text-gray-500">{formatConversationTime(c.last_message_at)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="p-2 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={loadTestAgentConversations}
              disabled={loadingTestList}
              className="w-full py-2 text-sm text-[#00A884] hover:bg-gray-100 rounded disabled:opacity-50"
            >
              {loadingTestList ? 'Loading...' : 'Refresh list'}
            </button>
          </div>
        </div>

        {/* Right: Chat area */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 rounded-r-xl overflow-hidden">
        {/* Chat header - minimal */}
        <div className="bg-[#202C33] px-3 py-2 flex items-center justify-between border-b border-[#2A3942]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm text-gray-300 truncate" title={sessionId}>
              {getConversationShortName(getConversationId()) || 'New chat'}
            </span>
            <button
              type="button"
              onClick={handleAddConversationToPlan}
              disabled={isRunningPlan}
              className="px-2 py-1 text-xs rounded bg-[#2A3942] text-white hover:bg-[#3d4a52] flex-shrink-0"
            >
              Add to test plan
            </button>
            {isRunningPlan && (
              <span className="text-xs text-[#00A884] truncate">{runProgressText || 'Running plan...'}</span>
            )}
          </div>
          <span className="text-xs text-gray-500 ml-2">Clinic: {clinic.id}</span>
        </div>

        {/* WhatsApp-style chat area: dark background with subtle pattern */}
        <div
          className="relative flex-1 min-h-0 overflow-y-auto flex flex-col"
          style={{
            backgroundColor: '#0B141A',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 6c-2 0-4 2-4 4s2 4 4 4 4-2 4-4-2-4-4-4zm0 40c-2 0-4 2-4 4s2 4 4 4 4-2 4-4-2-4-4-4z' fill='%231a2332' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }}
        >
          {chatHistory.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <p className="text-gray-500 text-sm">No messages yet. Start a conversation with the agent.</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {chatHistory.map((message, index) => {
                const isUser = message.role === 'user';
                const isAssistant = message.role === 'assistant';
                const isSystem = message.role === 'system';
                return (
                  <div
                    key={index}
                    className={`flex ${isUser ? 'justify-end' : isSystem ? 'justify-center' : 'justify-start'}`}
                  >
                    <div
                      className={`rounded-lg px-3 py-2 max-w-[85%] shadow-md ${
                        isUser
                          ? 'bg-[#005C4B] text-white rounded-br-md'
                          : isAssistant
                            ? 'bg-[#202C33] text-white rounded-bl-md'
                            : 'bg-red-900/80 text-red-100 rounded'
                      }`}
                    >
                      {isUser && (
                        <>
                          <div className="text-sm whitespace-pre-wrap break-words">{message.content}</div>
                          <div className="flex justify-end items-center gap-1 mt-1">
                            <span className="text-[10px] text-white/80">{formatBubbleTime()}</span>
                            <span className="text-white/90" title="Read">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 16 12" fill="currentColor">
                                <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51z" />
                                <path d="M6.727 3.316l-.478-.372a.365.365 0 0 0-.51.063L.484 9.88a.32.32 0 0 0 .036.54l1.32 1.265c.143.14.36.125.483-.033l6.272-8.048a.365.365 0 0 0-.063-.51l-.478-.372a.365.365 0 0 0-.51.063l-5.76 6.272a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.266c.143.14.361.125.484-.033l5.76-6.272a.366.366 0 0 0-.063-.51z" />
                              </svg>
                            </span>
                          </div>
                        </>
                      )}
                      {isAssistant && (() => {
                        if (message.image_data) {
                          const url = message.image_data.url || (message.image_data.base64 && message.image_data.content_type ? `data:${message.image_data.content_type};base64,${message.image_data.base64}` : null);
                          return (
                            <>
                              {url ? <img src={url} alt="Agent" className="max-w-full max-h-48 rounded" /> : <div className="text-sm text-gray-400">[Image]</div>}
                              <div className="text-[10px] text-white/70 mt-1">{formatBubbleTime()}</div>
                            </>
                          );
                        }
                        if (message.is_reply_with_template && message.template_call) {
                          return (
                            <>
                              <TemplatePreview templateCall={message.template_call} clid={clinic?.id} onQuickReplyClick={submitWithMessage} isSubmitting={isSubmitting} />
                              <div className="text-[10px] text-white/70 mt-1">{formatBubbleTime()}</div>
                            </>
                          );
                        }
                        if (message.is_interrupted && message.interrupted_tool_calls?.length > 0) {
                          return (
                            <>
                              <div className="text-sm whitespace-pre-wrap text-white">
                                {message.content || 'Please confirm the following message information, once confirmed, I will forward it to the clinic:'}
                              </div>
                              <div className="mt-3 flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => handleInterruptAction(message, true)}
                                  disabled={isSubmitting}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#00A884] text-white rounded-full text-sm font-medium hover:bg-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                  </svg>
                                  Send
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleInterruptAction(message, false)}
                                  disabled={isSubmitting}
                                  className="ml-2 px-3 py-2 bg-[#2A3942] text-gray-300 rounded-full text-sm hover:bg-[#3d4a52] disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </div>
                              <div className="text-[10px] text-white/70 mt-1">{formatBubbleTime()}</div>
                            </>
                          );
                        }
                        return (
                          <>
                            <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                            <div className="text-[10px] text-white/70 mt-1">{formatBubbleTime()}</div>
                          </>
                        );
                      })()}
                      {isSystem && <div className="text-sm whitespace-pre-wrap">{message.content}</div>}
                    </div>
                  </div>
                );
              })}
              {isSubmitting && (
                <div className="flex justify-start">
                  <div className="rounded-lg px-3 py-2 bg-[#202C33] text-white flex items-center gap-2 rounded-bl-md">
                    <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-xs text-gray-400">Agent is thinking...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        {apiError && (
          <div className="bg-red-900/90 text-red-100 px-3 py-2 text-sm flex items-center justify-between gap-2 border-t border-[#2A3942]">
            <span>{apiError}</span>
            <button type="button" onClick={() => setApiError(null)} className="text-red-200 hover:text-white p-1" aria-label="Dismiss">×</button>
          </div>
        )}

        {/* WhatsApp-style input bar */}
        <div className="bg-[#202C33] px-3 py-2 flex items-center gap-2 border-t border-[#2A3942]">
          <button type="button" className="p-2 text-gray-400 hover:text-gray-200 rounded-full hover:bg-[#2A3942]" title="Emoji" aria-label="Emoji">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <form onSubmit={handleSubmitMessage} className="flex-1 flex items-center gap-2 min-w-0">
            <input
              type="text"
              value={userMessage}
              onChange={handleUserMessageChange}
              placeholder="訊息"
              className="flex-1 min-w-0 px-4 py-2.5 rounded-full bg-[#2A3942] text-white placeholder-gray-500 border-0 text-sm focus:ring-1 focus:ring-[#00A884] focus:outline-none"
              disabled={isSubmitting}
            />
            <button type="button" className="p-2 text-gray-400 hover:text-gray-200 rounded-full hover:bg-[#2A3942]" title="Attachment" aria-label="Attachment">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <button type="button" className="p-2 text-gray-400 hover:text-gray-200 rounded-full hover:bg-[#2A3942]" title="Camera" aria-label="Camera">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4M14 8v4" />
              </svg>
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !userMessage.trim()}
              className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00A884] text-white flex items-center justify-center hover:bg-[#06CF9C] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#00A884]"
              title="Send"
              aria-label="Send"
            >
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>
      <ApiTestPlanPanel
        isOpen={isPlanModalOpen}
        onClose={handleClosePlanModal}
        clinicId={clinic?.id}
        plans={testPlans}
        selectedPlanId={selectedPlanId}
        onSelectPlan={setSelectedPlanId}
        onSavePlan={handleSavePlan}
        onDeletePlan={handleDeletePlan}
        onRunPlan={handleRunPlan}
        onStopRun={handleStopPlanRun}
        onGenerateExpectedReport={handleGenerateExpectedReport}
        isRunningPlan={isRunningPlan}
        runProgressText={runProgressText}
        pendingConversationCase={pendingConversationCase}
        onConsumePendingConversationCase={() => setPendingConversationCase(null)}
        pendingConversationCases={pendingConversationCases}
        onConsumePendingConversationCases={() => setPendingConversationCases(null)}
        pendingFocusPlanCase={pendingFocusPlanCase}
        onConsumePendingFocusPlanCase={() => setPendingFocusPlanCase(null)}
        autoCloseOnSave={autoClosePlanModalOnSave}
      />
      <ApiTestCasePickerModal
        isOpen={isCasePickerOpen}
        onClose={() => setIsCasePickerOpen(false)}
        onConfirm={handleConfirmCasePicker}
        cases={selectedPlan?.cases || []}
        initialSelection={runCaseIndices}
        failedLastRunIndices={lastRunFailedIndices}
        planName={selectedPlan?.name}
      />
      <ApiTestRunReportModal
        isOpen={isRunReportModalOpen}
        onClose={() => setIsRunReportModalOpen(false)}
        report={runReport}
        persistedMeta={loadedReportMeta}
        savedReports={savedReports}
        loadingSavedReports={loadingSavedReports}
        onSelectSavedReport={handleSelectSavedReport}
        onSaveCurrentReport={handleSaveCurrentRunReport}
        onDeleteSavedReport={handleDeleteSavedReport}
        onUpdateNotes={handleUpdateRunReportNotes}
        onUpdateName={handleUpdateRunReportName}
        onOpenConversation={handleOpenReportConversation}
        onMutateExchange={handleMutateReportExchange}
        onSetCaseReviewed={handleSetReportCaseReviewed}
        onDeleteCase={handleDeleteReportCase}
        onEditCaseInPlan={handleEditReportCaseInPlan}
        onRerunCases={handleRerunReportCases}
        rerunningCaseIndices={rerunningCaseIndices}
        clinicName={clinic?.name || clinic?.id}
      />
    </div>
  );
};

export default ApiTestSection;
